import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook to manage the tutorial flow
 * Tracks step progression and completion status
 * 
 * @param {boolean} firstTimeUser - Whether this is the user's first time using the app
 * @param {boolean} isWelcomeAnimationComplete - Whether the welcome animation has completed
 * @returns {Object} Tutorial state and control functions
 */
const useTutorial = (firstTimeUser, isWelcomeAnimationComplete) => {
  // Check if tutorial has been completed before
  const [isTutorialComplete, setIsTutorialComplete] = useState(() => {
    const completed = localStorage.getItem('pokket_tutorial_completed');
    return completed === 'true';
  });
  
  // Track whether tutorial mode is active
  const [isTutorialActive, setIsTutorialActive] = useState(false);
  
  // Track current step (1-5)
  const [currentStep, setCurrentStep] = useState(1);
  
  // Define tutorial content for each step
  const tutorialSteps = [
    {
      id: 1,
      target: 'suggestions',
      text: "These are suggested responses based on common issues players have when talking to Pokket. However, you can input your own message in the chat field below!"
    },
    {
      id: 2,
      target: 'chatInput',
      text: "This is where you can type to Pokket, or click the microphone button to use your voice instead!"
    },
    {
      id: 3,
      target: 'confidence',
      text: "Here Pokket will tell you how close they are to giving advice."
    },
    {
      id: 4,
      target: 'none',
      text: "Remember, Pokket needs to get to know you in order to help. The more detailed your responses, the more effective Pokket will be."
    }
  ];
  
  // Start tutorial when welcome animation completes and it's a first time user
  useEffect(() => {
    if (firstTimeUser && isWelcomeAnimationComplete && !isTutorialComplete) {
      setIsTutorialActive(true);
      setCurrentStep(1);
    }
  }, [firstTimeUser, isWelcomeAnimationComplete, isTutorialComplete]);
  
  // Move to the next tutorial step
  const goToNextStep = useCallback(() => {
    if (currentStep < tutorialSteps.length) {
      setCurrentStep(currentStep + 1);
    } else {
      // Tutorial complete
      completeTutorial();
    }
  }, [currentStep, tutorialSteps.length]);
  
  // Move to the previous tutorial step
  const goToPreviousStep = useCallback(() => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);
  
  // Complete the tutorial
  const completeTutorial = useCallback(() => {
    setIsTutorialActive(false);
    setIsTutorialComplete(true);
    localStorage.setItem('pokket_tutorial_completed', 'true');
  }, []);
  
  // Skip the tutorial entirely
  const skipTutorial = useCallback(() => {
    completeTutorial();
  }, [completeTutorial]);
  
  // Reset the tutorial (for testing purposes)
  const resetTutorial = useCallback(() => {
    localStorage.removeItem('pokket_tutorial_completed');
    setIsTutorialComplete(false);
  }, []);
  
  // Get the current step data
  const currentStepData = tutorialSteps[currentStep - 1] || tutorialSteps[0];
  
  return {
    isTutorialActive,
    isTutorialComplete,
    currentStep,
    currentStepData,
    totalSteps: tutorialSteps.length,
    goToNextStep,
    goToPreviousStep,
    skipTutorial,
    resetTutorial,
    setIsTutorialActive
  };
};

export default useTutorial; 